import { Component, Input, OnInit } from '@angular/core';
import { ApplicationAccessSpecifier, LoanApplication } from '@app/applications/applications.model';
import { ApplicationService } from '../../../application.service';
import { SecureStorageService } from '@ig-core/authentication/secureStorage.service';
import { MatDialog, MatSnackBar } from '@angular/material';
import { ComparisionViewComponent } from '@app/utils/comparison-view/comparision-view.component';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { ConfirmationDialogService } from '@app/utils/confirmation-dialog/confirmation-dialog.service';
@Component({
  selector: 'eng-compare-question',
  templateUrl: './compare-question.template.html',
  styleUrls: ['../../application-details.styles.scss', './compare-question.styles.scss']
})
export class CompareQuestionComponent implements OnInit {
  @Input() application: LoanApplication;
  @Input() menuCode: string;

  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;
  allowAccess: boolean;
  menuItemAllowAccess: boolean;
  roles: string[] = [];
  sectionList: any[] = [];
  comparisionData: any[] = [];
  assetConfig: any[] = [];
  assessorList: any[] = [];
  loginUser: any;
  showFullScreenTable = false;
  isFormDisabled: boolean;
  // default value is 100
  ellipsisCharLimit: number = 100;
  comparisionDataPrestine: any;
  bureauResponse: any;
  mandatoryQuestionList: any[] = [];
  currentFocusedField: number = -1;
  unansweredRequiredFields: any[] = [];
  firstMandatoryFieldIndex: number = -1;
  lastMandatoryFieldIndex: number = -1;
  allowedCloneRoles: string[] = [];

  constructor(private applicationService: ApplicationService,
    private secureStorageService: SecureStorageService,
    private _snackbar: MatSnackBar,
    private dialog: MatDialog,
    private referenceCodeService: ReferenceCodeService,
    private confirmationDialogService: ConfirmationDialogService
  ) { }

  ngOnInit() {
    this.getBureauDetails();
    this.getMenuItemAccess();
    this.isFormDisabled = true;
    this.loginUser = this.secureStorageService.secureSessionStorage.getItem("logInUserDetails")
    this.getAssetConfig();
    this.allowAccess = this.applicationService.allowAccess;
  }

  getBureauDetails() {
    this.applicationService.getBureauScore(this.application.uid).subscribe((response: any) => {
      this.bureauResponse = response.body;
      this.bureauResponse.forEach(applicant => {
        applicant.bureauInformation.sort((a, b) =>
          a.bureauReportName.localeCompare(b.bureauReportName)
        );
      });
    });
  }

  getAssetConfig() {
    this.applicationService.getAssessmentCompareConfig(this.application.uid).subscribe(response => {
      this.assetConfig = response.body.assessmentQuestionsGroupBySection;
      this.allowedCloneRoles = (response.body.cloneSourceRoles || '')
        .split(',')
        .map(role => role.trim().toUpperCase())
        .filter(role => role);

      this.getRefCodes();
    });
  }

  getRefCodes() {
    this.referenceCodeService.getRefCodesForClassifier('assessment_settings').subscribe((response: any) => {
      response.forEach(element => {
        if (element.code === "MAX_CHAR_DISPLAY_LIMIT") {
          if (element && element.field1 !== null && element.field1 !== undefined) {
            var num = Number(element.field1);
            if (!isNaN(num) && num > 0) {
              this.ellipsisCharLimit = num;
            }
          }
        }
      });
    });
    this.getComparisionData();
  }

  getComparisionData() {
    this.applicationService.getAssessmentComposit(this.application.uid).subscribe(response => {
      this.comparisionData = response.body;
      // on click of cancel we need to show the original data, hence we are creating a copy
      this.comparisionDataPrestine = JSON.parse(JSON.stringify(this.comparisionData));
      this.builComparisonViewData(this.comparisionData, this.assetConfig);
    });
  }

  builComparisonViewData(apiResponse, sectionConfig) {

    const assessorList = apiResponse.map(user => ({
      role: user.role,
      assessedBy: user.assessedBy,
      label: user.assessedBy ? `${user.role} (${user.assessedBy})` : user.role
    }));

    const sectionList = [];
    this.mandatoryQuestionList = [];

    sectionConfig.forEach(configSection => {

      //if same question code then we need to add the quection in the same section not as different section 
      let sectionData = sectionList.find(
        sec => sec.sectionCode === configSection.sectionCode
      );

      if (!sectionData) {
        sectionData = {
          sectionName: configSection.section,
          sectionCode: configSection.sectionCode,
          questions: []
        };
        sectionList.push(sectionData);
      }

      configSection.fields
        .forEach(configField => {

          const questionRow = {
            questionText: configField.displayName,
            answers: [],
            order: configField.order,
            required: configField.required === true,
            requiredForRole: configField.requiredForRole
          };

          apiResponse.forEach(assessment => {

            let currentSectionData = assessment;

            // Traverse nested path (e.g., "businessDetails.financials")
            configSection.source.split('.').forEach(pathKey => {
              currentSectionData = currentSectionData && currentSectionData[pathKey];
            });

            const referenceObject = currentSectionData || {};

            const answerObj = {
              role: assessment.role,
              assessedBy: assessment.assessedBy,
              fieldCode: configField.code,
              assessmentData: referenceObject
            };

            questionRow.answers.push(answerObj);
            if (configField.required === true && assessment.assessedBy === this.loginUser.login) {
              this.mandatoryQuestionList.push({
                questionText: configField.displayName,
                requiredForRole: configField.requiredForRole,
                answer: answerObj,
                question: questionRow
              });
            }
          });

          sectionData.questions.push(questionRow);

        });

    });
    this.assessorList = assessorList;

    this.sectionList = sectionList.map(section => ({
      ...section,
      questions: section.questions.sort((a, b) => a.order - b.order),
      open: true
    }));
  }

  saveRemarks() {
    if (this.isFormDisabled) {
      return
    }

    const user = this.comparisionData
      .find(u => u.assessedBy === this.loginUser.login);

    this.trimAllStrings(user.customerAssessmentDetailsDTO);

    this.applicationService
      .saveReviewRemarks(this.application.uid, user.customerAssessmentDetailsDTO)
      .subscribe(() => {
        this.comparisionDataPrestine = JSON.parse(JSON.stringify(this.comparisionData));
        this.isFormDisabled = true;
        this.resetIndex();
        this._snackbar.open("Remarks saved successfully", "Close", {
          duration: 5000,
        });
      });
  }

  // this is used to check the field is mandatory or not and to show that particular field in red
  isFieldInvalid(question, answerItem): boolean {
    if (this.isFormDisabled) return false;

    // we are showing the error only for the user who is logged in not for the disabled fields
    if (!answerItem || answerItem.assessedBy !== this.loginUser.login) {
      return false;
    }

    if (!this.isQuestionRequired(question)) return false;

    const value = answerItem.assessmentData && answerItem.assessmentData[answerItem.fieldCode];

    return (value === null || value === undefined || value.toString().trim() === '');
  }

  // this is used to show * for required fields
  isQuestionRequired(question): boolean {
    if (!question || !question.required) { return false; }

    if (question.requiredForRole && question.requiredForRole.trim()) {

      const allowedRoles = question.requiredForRole.split(',').map(role => role.trim().toUpperCase()).filter(role => role);

      return allowedRoles.includes(this.loginUser.role.toUpperCase());
    }
    // if no role restriction then mandatory for all
    return true;
  }

  trimAllStrings(obj: any): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    Object.keys(obj).forEach(key => {
      const value = obj[key];

      if (typeof value === 'string') {
        obj[key] = value.trim();
      } else if (value && typeof value === 'object') {
        this.trimAllStrings(value);
      }
    });
  }

  enableDisableForm() {
    this.isFormDisabled = false;
    this.initializeValues();
  }

  resetIndex() {
    this.currentFocusedField = -1;
    this.firstMandatoryFieldIndex = -1;
    this.lastMandatoryFieldIndex = -1;
    this.unansweredRequiredFields = [];
  }

  initializeValues() {
    let fieldIndex = 0;
    const unAnsweredRequiredFields: any[] = [];

    this.sectionList.forEach(section => {
      section.questions.forEach(question => {
        question.answers.forEach(answer => {

          if (answer.assessedBy === this.loginUser.login) {
            answer.fieldIndex = fieldIndex++;
            if (this.isFieldInvalid(question, answer)) {
              answer.parentSection = section;
              unAnsweredRequiredFields.push(answer);
            }
          }
        });
      });
    });
    this.unansweredRequiredFields = unAnsweredRequiredFields;

    if (this.unansweredRequiredFields.length > 0) {
      this.firstMandatoryFieldIndex = this.unansweredRequiredFields[0].fieldIndex;
      this.lastMandatoryFieldIndex = this.unansweredRequiredFields[this.unansweredRequiredFields.length - 1].fieldIndex;
    }
  }

  navigateToMandatoryField(direction: 'next' | 'previous') {
    let nextOrPrevInvalidAnswer;

    if (direction === 'next') {
      nextOrPrevInvalidAnswer = this.unansweredRequiredFields.find(answer =>
        answer.fieldIndex > this.currentFocusedField);
    } else {
      nextOrPrevInvalidAnswer = this.unansweredRequiredFields.slice().reverse().find(answer =>
        answer.fieldIndex < this.currentFocusedField);
    }
    if (nextOrPrevInvalidAnswer) {
      // Automatically force-expand the parent accordion if it is hidden/collapsed
      if (!nextOrPrevInvalidAnswer.parentSection.open) {
        nextOrPrevInvalidAnswer.parentSection.open = true;
      }
      // A minor delay gives Angular time to repaint 
      // the elements to the DOM if a section was previously closed.
      setTimeout(() => {
        const targetElement = document.getElementById(`textarea-${nextOrPrevInvalidAnswer.fieldIndex}`);

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          (targetElement as HTMLElement).focus();
          this.currentFocusedField = nextOrPrevInvalidAnswer.fieldIndex;
        }
      });
    }
  }

  cancelForm() {
    this.resetIndex();
    this.comparisionData = JSON.parse(JSON.stringify(this.comparisionDataPrestine));

    // update ONLY values inside existing structure
    this.sectionList.forEach(section => {
      section.questions.forEach(question => {
        question.answers.forEach(answer => {

          const user = this.comparisionData.find(
            u => u.assessedBy === answer.assessedBy
          );

          let currentSectionData = user;

          const configSection = this.assetConfig.find(
            s => s.sectionCode === section.sectionCode && s.fields.some(f => f.code === answer.fieldCode)
          );

          configSection.source.split('.').forEach(pathKey => {
            currentSectionData = currentSectionData && currentSectionData[pathKey];
          });
          // we have data in assessmentData so if we replace only assessmentData that is enough. 
          // No need of again replacing role,assessedBy,fieldCode values.
          answer.assessmentData = currentSectionData || {};
        });
      });
    });
    this.isFormDisabled = true;
  }

  copyAssessment(assessor): void {
    this.confirmationDialogService.open({ "btnCancelText": "No", "btnConfirmText": "Yes", "message": "Copy review remarks from " + assessor.label + "? This action will replace any existing review remarks entered by you." })
    this.confirmationDialogService.confirmed().subscribe(confirm => {
      if (confirm) {
        this.mandatoryQuestionList.forEach(item => {
          const sourceAnswer = item.question.answers.find(
            answer => answer.assessedBy === assessor.assessedBy);

          if (sourceAnswer) {
            item.answer.assessmentData[item.answer.fieldCode] =
              sourceAnswer.assessmentData[sourceAnswer.fieldCode];
          }
        });
        this.initializeValues();
      }
    });
  }

  toggleFullScreenTable() {
    // resetting the values before opening the full view component
    this.isFormDisabled = true;
    const dialogRef = this.dialog.open(ComparisionViewComponent, {
      width: '95vw',
      height: '95vh',
      maxWidth: 'none',
      data: {
        assessors: this.assessorList,
        comparisionAssessmentData: this.sectionList,
        loginUser: this.loginUser,
        allowAccess: this.allowAccess,
        menuItemAllowAccess: this.menuItemAllowAccess,
        mandatoryQuestionList: this.mandatoryQuestionList,
        allowedCloneRoles : this.allowedCloneRoles
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res.saved) {
        // on save we are checking for form enabled, so if user clicks full screen without clicking edit 
        // then form will be disabled. So before saving we are enabling the form 
        this.isFormDisabled = false;
        this.saveRemarks();
      } else {
        this.cancelForm();
      }
    });
  }

  truncate(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const str = String(value).trim();

    return str.length > this.ellipsisCharLimit ? str.slice(0, this.ellipsisCharLimit) + '...' : str;
  }

  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }
}
